/**
 * ホームの C（既定・homeFlow.ts HOME_VARIANT = 'c'・2026-09-28 管理の方針）。道具としての素直な画面。
 *   見出しは場所の名前「プロジェクト」だけ（Vercel・Cloudflare の一覧の画面と同じ考え）。決め台詞・柱・「用意済み」は出さない。
 *   プロジェクトが 0: 説明 1 行 → 番号つきの手順（つなぐ・[確認]・[入金]・頼み方の例）→「プロジェクトを作る」
 *   プロジェクトがある: 見出しの横に「プロジェクトを作る」、欠けている準備があるときだけ手順を小さく、あとは一覧
 * 「ゲーム」ではなく「プロジェクト」（2026-09-28 founder の方針・MASTER-PLAN §0「Kumo はゲームだけじゃない。アプリでも何でも。
 *   バイブコーディングの人のためのバックエンド」）。頼み方の例もアプリとゲームを混ぜる。API の名前（players 等）と奥の画面の言葉は別の作業
 * 経緯: A「もう、全部そろっています」はダサい → B「何を作る？」は意味がわからん・キャッチコピーは要らない（founder）。
 * 言葉はどれも「何が起きるか」をそのまま書く（i18n の home.c.* / home.step.* / home.agent.*）。
 * 頼み方の例は homeFlow.ts buildAsk ＋ home.askTemplate で作る（Skill「start」ができたらこの 2 か所だけ差し替える）。製品名は入れない。
 *   表示は components/AskExamples.tsx（プロジェクトの概要と共通・2026-09-30 console/core-first）
 * 2026-10-14 gtm/onboarding（10/14 本番: 招待で登録 9 人 → AI をつないだ 2 人。登録直後に止まる）:
 *   - 手順は「いまの 1 段だけ開く」（homeFlow.ts stepViewsC）。済み = チェックだけ・その先 = 題だけ薄く。以前は全部の段を開いて
 *     ボタン 7 つ・コピーの欄 5 つが一度に並んでいた（docs/teams/gtm/screenshots/before-01-unverified.png）
 *   - 開いている段が「確認」「つなぐ」なら、画面が自分で進むことを 1 行で言う（読み直しは ProjectsPage）
 *   - 「頼む」の段は最初の 1 文（home.askExample.first）を大きく、4 つの例は「ほかのもの」として下に（作り手が選べる）
 *   - 手で作る「プロジェクトを作る」は、準備の段（確認・つなぐ・入金）の間は控えめ（ghost）にする（主なボタンを 1 つにする）
 */
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ProjectSummary } from '../api';
import { ResendVerificationButton } from '../components/EmailUnverified';
import { INVITE_CODE_PATH } from '../components/InviteNotAppliedNotice';
import { AskExamples } from '../components/AskExamples';
import { ConnectPictures } from '../components/ConnectPictures';
import { CopyButton } from '../components/ui';
import { CONNECT_PATH } from '../connectPage';
import { homeStepsC, inviteVerifyAmount, stepViewsC, type HomeInput, type StepCId } from '../homeFlow';
import { useT } from '../i18n';
import { cameFromAd } from '../adVisit';
import { deviceStorage } from '../lastLogin';
import { stepShownFor } from '../onboardingSteps';
import { reportOnboardingStep } from '../onboardingReport';
import { ConnectBody, ProjectGrid, useVerifyBody } from './HomeParts';

export function HomeC({
  projects,
  flow,
  mcpUrl,
  creating,
  onCreate,
  createForm,
  firstTest = null
}: {
  projects: ProjectSummary[];
  flow: HomeInput;
  mcpUrl: string | null;
  creating: boolean;
  onCreate: () => void;
  createForm: ReactNode;
  firstTest?: { name: string; url: string } | null;
}) {
  const { t } = useT();
  const hasGames = projects.length > 0;
  const steps = homeStepsC(flow);
  const views = stepViewsC(steps);
  const openId = steps[views.indexOf('open')]?.id;
  // オンボーディングの段（onboardingSteps.ts・2026-10-18 ops/gaps-activity）: 開いた段を 1 画面の読み込みにつき 1 回知らせる。「つなぐ」は ConnectBody が知らせる
  useEffect(() => {
    const step = stepShownFor(openId);
    if (step) reportOnboardingStep(step);
  }, [openId]);
  // 準備の段（確認・つなぐ・入金）が開いている間は、手で作るボタンを控えめに（いまの段の主なボタン・コピーと競わせない）。消しはしない（作り手が選べる）
  const createPrimary = !hasGames && (openId === undefined || openId === 'ask');
  const createButton = !creating && (
    <button type="button" className={`btn ${createPrimary ? 'btn-primary' : 'btn-ghost'}`} onClick={onCreate}>
      {t('home.c.create')}
    </button>
  );

  return (
    <div className={`home home-c ${hasGames ? 'home-compact' : ''}`}>
      <header className="home-c-head">
        <h1 className="home-c-title">{t('home.c.title')}</h1>
        {/* 以前はここに使い方ガイドへのリンク（2026-10-07 console/guide-page）。上の帯の「How to use」と同じ行き先が 1 画面に 2 つ並んだので外した
            （2026-10-18 design/rauno-dashboard2・rauno-audit.md ホームの行）。スマホでは上の帯のメニューの中に名前つきで出る */}
        {hasGames && createButton}
      </header>
      {!hasGames && <p className="home-c-empty">{t('home.c.empty')}</p>}
      {firstTest && <FirstTestCard name={firstTest.name} url={firstTest.url} />}

      {steps.length > 0 && (
        <section aria-label={hasGames ? t('home.c.setupTitle') : t('home.c.stepsLabel')}>
          {hasGames && <h2 className="home-section-title">{t('home.c.setupTitle')}</h2>}
          <ol className="home-c-steps">
            {steps.map((s, i) => (
              <li key={s.id} className={`home-c-step is-${views[i]}`} aria-current={views[i] === 'open' ? 'step' : undefined}>
                <span className="home-step-mark" aria-hidden="true">
                  {s.done ? (
                    <svg viewBox="0 0 16 16" width="12" height="12">
                      <path d="M3 8.5l3 3 7-7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </span>
                <div className="home-c-step-main">
                  <h3 className="home-c-step-title">
                    {stepTitle(s.id, t)}
                    {s.done && <span className="home-step-state">{t(s.id === 'connect' ? 'home.c.connected' : 'home.stepDone')}</span>}
                  </h3>
                  {views[i] === 'open' && <StepCBody id={s.id} mcpUrl={mcpUrl} invite={inviteVerifyAmount(flow)} />}
                  {/* つないだ（済み）の段にも、1 行をもう一度出す画面（/connect）への道（2026-10-07 console/connect-again）。
                      済んだ段は 1 行を畳んでしまうので、別の PC・別の AI エージェントで使い始める人が迷わないように */}
                  {s.done && s.id === 'connect' && (
                    <p className="home-connect-again">
                      <Link to={CONNECT_PATH}>{t('connect.elsewhereLink')}</Link>
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {!hasGames && (
        <div className="home-c-create">
          {createButton}
          {createForm}
        </div>
      )}

      {hasGames && (
        <section aria-label={t('home.c.title')}>
          {createForm}
          <ProjectGrid projects={projects} />
        </section>
      )}
    </div>
  );
}

function stepTitle(id: StepCId, t: ReturnType<typeof useT>['t']): string {
  if (id === 'ask') return t('home.c.askTitle');
  return t(`home.step.${id}.title`);
}

function StepCBody({ id, mcpUrl, invite }: { id: StepCId; mcpUrl: string | null; invite?: { granted: number; currency: string } | null }) {
  const { t } = useT();
  const verifyBody = useVerifyBody(invite);
  switch (id) {
    case 'verify':
      return (
        <>
          <p>{verifyBody}</p>
          <div className="home-step-actions">
            <ResendVerificationButton className="btn btn-ghost btn-sm" />
          </div>
          <Waiting text={t('home.c.verifyWaiting')} />
        </>
      );
    case 'connect':
      return (
        <>
          <p>{t('home.c.connectBody')}</p>
          <ConnectBody mcpUrl={mcpUrl} />
          {/* 貼った後の画面の絵（AI ごとの 4 段・2026-10-19 design/visual-guides）。止まる所 = 貼った後に何が出てどこを押すか、を先に見せる */}
          <ConnectPictures />
          <Waiting text={t('home.c.connectWaiting')} />
        </>
      );
    case 'topUp':
      return (
        <>
          <p>{t('home.step.topUp.body')}</p>
          <div className="home-step-actions">
            <Link to="/money/prepaid" className="btn btn-ghost btn-sm">
              {t('home.step.topUp.cta')}
            </Link>
            {/* 招待コードのある人の道（2026-10-29 core/open-signup-build・HomeParts の topUp と同じ）。
                2026-10-29 core/open-signup-polish（Rauno 役）: ボタンではなく文字のリンク = 段のボタンは「入金する」1 つ（同じ重さのボタンが 2 つ並ぶと読む順が決まらない） */}
            <Link to={INVITE_CODE_PATH} className="home-step-link">
              {t('invite.haveCode')}
            </Link>
          </div>
        </>
      );
    case 'ask':
      return (
        <>
          <p>{t('home.c.askBody')}</p>
          <FirstAsk />
          <AskExamples />
        </>
      );
  }
}

/**
 * 「待っています・自動で進みます」の 1 行（確認・つなぐの段）。role=status = 読み上げにも伝わる。点の動きは CSS（動きを減らす設定なら止める）
 * WHY: 別のタブ・別のアプリ（AI）で何かしたあと、元の画面が変わらないと「できたのか」が分からず止まる
 */
function Waiting({ text }: { text: string }) {
  return (
    <p className="home-c-waiting" role="status">
      <span className="home-c-waiting-dot" aria-hidden="true" />
      {text}
    </p>
  );
}

/**
 * 最初の 1 つの頼み文（大きく・コピーつき）と、テスト版から始まる事実の 1 行。下の AskExamples の見出しは「ほかのものを頼むなら」になる
 * （AskExamples は概要の画面とも共通なので中は変えない。ここで自分の見出しを出し、すぐ後ろの元の見出し「頼み方の例」は CSS で隠す = .home-c-first + .home-ask-label）
 */
function FirstAsk() {
  const { t } = useT();
  // 広告から来た人（adVisit.ts の印・2026-10-18 gtm/early-fixes）は「このゲームを公開して」を一番上に、まだゲームが無い人向けに今までの頼みを下に 1 行。
  //   ほかの入口の人は今までの頼み（home.askExample.first）だけ
  const ownGame = cameFromAd(deviceStorage(), Date.now());
  const ask = t(ownGame ? 'home.askExample.firstAd' : 'home.askExample.first');
  return (
    <div className="home-c-first">
      <p className="home-ask-label">{t('home.c.firstAskLabel')}</p>
      <div className="home-c-first-ask">
        <span className="home-example-text">{ask}</span>
        <CopyButton text={ask} onCopy={() => reportOnboardingStep('first_ask_copied')} />
      </div>
      {ownGame && <p className="home-c-first-note">{t('home.askExample.noGameYet')}</p>}
      <p className="home-c-first-note">{t('home.c.firstAskNote')}</p>
      <p className="home-ask-label home-c-more-label">{t('home.c.moreExamplesLabel')}</p>
    </div>
  );
}

/**
 * 最初のテスト版のカード（2026-10-15 gtm/onboarding・活性化の瞬間を大きく見せる）。遊ぶボタン（新しいタブ）と、次の 1 歩
 * （AI に「みんなに公開して」と言う・一度だけ確認が出る）。URL は homeFlow.ts firstTestUrl で形を確かめ済み
 */
function FirstTestCard({ name, url }: { name: string; url: string }) {
  const { t } = useT();
  const ask = t('home.c.firstTest.ask');
  // オンボーディングの段（S5・onboardingSteps.ts）: 最初のテスト版のカードが出た
  useEffect(() => reportOnboardingStep('first_test_seen'), []);
  return (
    <section className="home-c-firsttest" aria-label={t('home.c.firstTest.title')}>
      <p className="home-c-firsttest-eyebrow">{t('home.c.firstTest.title')}</p>
      <h2 className="home-c-firsttest-name">{name}</h2>
      <div className="home-c-firsttest-actions">
        <a className="btn btn-primary" href={url} target="_blank" rel="noreferrer noopener" onClick={() => reportOnboardingStep('first_test_played')}>
          {t('home.c.firstTest.play')} ↗
        </a>
        <span className="home-c-firsttest-url">{url}</span>
      </div>
      <p className="home-c-firsttest-next">{t('home.c.firstTest.next')}</p>
      <div className="home-c-first-ask">
        <span className="home-example-text">{ask}</span>
        <CopyButton text={ask} onCopy={() => reportOnboardingStep('publish_ask_copied')} />
      </div>
      <p className="home-c-first-note">{t('home.c.firstTest.note')}</p>
    </section>
  );
}
